import { useEffect, useImperativeHandle, useRef, forwardRef } from "react";
import SignaturePad from "signature_pad";

/** Touch-friendly signature pad. Parent gets clear() / toDataURL() via ref. */
const SignaturePadCanvas = forwardRef(function SignaturePadCanvas({ onEmptyChange, height = 220 }, ref) {
  const canvasRef = useRef(null);
  const padRef = useRef(null);

  const resize = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    const data = padRef.current?.toData?.() || [];
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    c.getContext("2d").scale(ratio, ratio);
    if (padRef.current) {
      padRef.current.clear();
      if (data.length) padRef.current.fromData(data);
    }
  };

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return undefined;
    padRef.current = new SignaturePad(c, {
      backgroundColor: "rgb(255,255,255)",
      penColor: "rgb(15,23,42)",
      minWidth: 1.2, maxWidth: 2.6, throttle: 12,
    });
    padRef.current.addEventListener("endStroke", () => onEmptyChange?.(padRef.current.isEmpty()));
    onEmptyChange?.(true);
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    clear: () => { padRef.current?.clear(); onEmptyChange?.(true); },
    toDataURL: () => padRef.current?.toDataURL("image/png"),
    isEmpty: () => padRef.current?.isEmpty() ?? true,
  }), [onEmptyChange]);

  return (
    <div className="space-y-2">
      <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white shadow-inner overflow-hidden" style={{ height }}>
        <canvas
          ref={canvasRef}
          className="w-full h-full block"
          style={{ touchAction: "none" }}
          data-testid="visitor-signature-canvas"
        />
      </div>
      <div className="text-xs text-slate-500">Sign with your finger.</div>
    </div>
  );
});

export default SignaturePadCanvas;
